import React, { useState } from 'react';
import { Button, ButtonGroup, Form, ToggleButton } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { getRecordsProgression } from '../api/trainer';
import { RecordDistance } from '../api/types';
import PageHeader from '../components/PageHeader';
import ProgressSmallMultiples from '../components/ProgressSmallMultiples';
import RecordsProgressChart from '../components/RecordsProgressChart';
import StatusMessage from '../components/StatusMessage';
import { isoDaysAgo } from '../utils/format';
import { useAsync } from '../hooks/useAsync';
import {
  API_TOP,
  buildSeries,
  dayNumber,
  distancesWithEfforts,
  minYearFor,
  ProgressMetric,
  ProgressView,
  RANGE_OPTIONS,
  TOP_OPTIONS,
  YearRange,
} from '../utils/progression';

/** all: every distance on one chart, grid: one small chart each, single: one distance on its own. */
type Mode = 'all' | 'grid' | 'single';
/** The metric of the all-distances chart: pace, or the gap to each distance's PB. */
type AllMetric = 'pace' | 'gap';

const RecordsProgressPage: React.FC = () => {
  const progress = useAsync(() => getRecordsProgression(API_TOP), []);
  const [view, setView] = useState<ProgressView>('pb');
  const [mode, setMode] = useState<Mode>('grid');
  const [top, setTop] = useState<number>(3);
  const [range, setRange] = useState<YearRange>('all');
  const [allMetric, setAllMetric] = useState<AllMetric>('gap');
  const [focus, setFocus] = useState<RecordDistance | null>(null);
  const [hover, setHover] = useState<RecordDistance | null>(null);
  const [singleCode, setSingleCode] = useState<RecordDistance | null>(null);

  const withEfforts = distancesWithEfforts(progress.data?.distances ?? []);
  const single = withEfforts.find(d => d.distance === singleCode) ?? withEfforts[0] ?? null;
  const metric: ProgressMetric = mode === 'single' || mode === 'grid' ? 'time' : allMetric;

  // Built from every distance with data, so a distance keeps its color when another is hidden.
  const allSeries = buildSeries(withEfforts, {
    view,
    top,
    metric,
    minYear: minYearFor(range, new Date().getFullYear()),
    today: dayNumber(isoDaysAgo(0)),
  });
  const visible = allSeries.filter(s => (mode === 'single' ? s.code === single?.distance : true));

  // Press a distance to pin it (the others fade), press it again to let go.
  const toggleFocus = (code: RecordDistance) => setFocus(current => (current === code ? null : code));
  const activeCode = hover ?? focus;

  const hasData = withEfforts.length > 0;

  return (
    <>
      <PageHeader title="Progression">
        <p>
          How your personal bests improved over time, one chart per distance. Switch to the yearly view for the top
          efforts of each year, or press a distance in the overlay chart to highlight it. Adjusted and manual times count
          too.
        </p>
        <div>
          <Link to="/records" className="btn btn-outline-secondary btn-sm">
            Back to records
          </Link>
        </div>
      </PageHeader>

      <StatusMessage loading={progress.loading} error={progress.error} onRetry={progress.reload} />

      {progress.data && !hasData && (
        <section className="surface">
          <p className="mb-0">
            No running efforts yet. Efforts appear once your running activities are synced, or when you add a manual time
            on the Records page.
          </p>
        </section>
      )}

      {progress.data && hasData && (
        <section className="surface">
          <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
            <ButtonGroup aria-label="Chart view">
              <ToggleButton
                id="progress-view-yearly"
                type="radio"
                variant="outline-primary"
                name="progress-view"
                value="yearly"
                checked={view === 'yearly'}
                onChange={() => setView('yearly')}
                size="sm"
              >
                Yearly best
              </ToggleButton>
              <ToggleButton
                id="progress-view-pb"
                type="radio"
                variant="outline-primary"
                name="progress-view"
                value="pb"
                checked={view === 'pb'}
                onChange={() => setView('pb')}
                size="sm"
              >
                All-time PB progression
              </ToggleButton>
            </ButtonGroup>

            <ButtonGroup aria-label="Distances shown">
              <ToggleButton
                id="progress-mode-all"
                type="radio"
                variant="outline-secondary"
                name="progress-mode"
                value="all"
                checked={mode === 'all'}
                onChange={() => setMode('all')}
                size="sm"
              >
                All distances
              </ToggleButton>
              <ToggleButton
                id="progress-mode-grid"
                type="radio"
                variant="outline-secondary"
                name="progress-mode"
                value="grid"
                checked={mode === 'grid'}
                onChange={() => setMode('grid')}
                size="sm"
              >
                Side by side
              </ToggleButton>
              <ToggleButton
                id="progress-mode-single"
                type="radio"
                variant="outline-secondary"
                name="progress-mode"
                value="single"
                checked={mode === 'single'}
                onChange={() => setMode('single')}
                size="sm"
              >
                Single distance
              </ToggleButton>
            </ButtonGroup>

            {mode === 'all' && (
              <ButtonGroup aria-label="Measure">
                <ToggleButton
                  id="progress-metric-gap"
                  type="radio"
                  variant="outline-secondary"
                  name="progress-metric"
                  value="gap"
                  checked={allMetric === 'gap'}
                  onChange={() => setAllMetric('gap')}
                  size="sm"
                  title="How far each result is from that distance's personal best"
                >
                  % off PB
                </ToggleButton>
                <ToggleButton
                  id="progress-metric-pace"
                  type="radio"
                  variant="outline-secondary"
                  name="progress-metric"
                  value="pace"
                  checked={allMetric === 'pace'}
                  onChange={() => setAllMetric('pace')}
                  size="sm"
                >
                  Pace
                </ToggleButton>
              </ButtonGroup>
            )}

            {mode === 'single' && single && (
              <Form.Select
                size="sm"
                className="w-auto"
                aria-label="Distance"
                value={single.distance}
                onChange={e => setSingleCode(e.target.value as RecordDistance)}
              >
                {withEfforts.map(d => (
                  <option key={d.distance} value={d.distance}>
                    {d.name}
                  </option>
                ))}
              </Form.Select>
            )}

            <Form.Select
              size="sm"
              className="w-auto"
              aria-label="Efforts per year"
              value={top}
              disabled={view === 'pb'}
              onChange={e => setTop(Number(e.target.value))}
              title={view === 'pb' ? 'Applies to the yearly view' : undefined}
            >
              {TOP_OPTIONS.map(n => (
                <option key={n} value={n}>
                  Top {n} per year
                </option>
              ))}
            </Form.Select>

            <Form.Select
              size="sm"
              className="w-auto"
              aria-label="Year range"
              value={range}
              onChange={e => setRange(e.target.value as YearRange)}
            >
              {RANGE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
          </div>

          {mode === 'all' && (
            <div className="progress-legend mb-3" role="group" aria-label="Distances" onMouseLeave={() => setHover(null)}>
              {allSeries.map(s => {
                const pinned = focus === s.code;
                const dimmed = activeCode !== null && activeCode !== s.code;
                return (
                  <Button
                    key={s.code}
                    size="sm"
                    variant="outline-secondary"
                    className={`progress-chip${dimmed ? ' is-dim' : ''}${pinned ? ' is-focus' : ''}`}
                    style={{ '--chip-color': s.style.color } as React.CSSProperties}
                    aria-pressed={pinned}
                    onClick={() => toggleFocus(s.code)}
                    onMouseEnter={() => setHover(s.code)}
                    onFocus={() => setHover(s.code)}
                    onBlur={() => setHover(null)}
                  >
                    <span
                      className="progress-swatch"
                      style={{ background: s.style.color }}
                      aria-hidden="true"
                    />
                    {s.name}
                  </Button>
                );
              })}
              <span className="small muted align-self-center">
                {focus ? 'Press the highlighted distance again to show all equally.' : 'Press a distance to highlight it.'}
              </span>
            </div>
          )}

          {visible.length === 0 ? (
            <p className="muted mb-0">
              {mode === 'all'
                ? 'No efforts in the chosen range.'
                : 'No efforts for this distance in the chosen range.'}
            </p>
          ) : (
            mode === 'grid' ? (
              <ProgressSmallMultiples
                series={visible}
                view={view}
                onOpen={code => {
                  setSingleCode(code as RecordDistance);
                  setMode('single');
                }}
              />
            ) : (
              <RecordsProgressChart
                series={visible}
                view={view}
                metric={metric}
                focus={mode === 'all' ? focus : null}
                hover={mode === 'all' ? hover : null}
                onHover={code => setHover(code as RecordDistance | null)}
              />
            )
          )}
        </section>
      )}
    </>
  );
};

export default RecordsProgressPage;
